import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area,
} from "recharts";
import { BarChart3, TrendingUp, Users, MapPin, Truck, Crown, ShoppingBag, Calendar, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";

const COLORS = ["#002FA7", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#0EA5E9"];

const RANGE_OPTIONS = [
  { value: "7", label: "Últimos 7 días" },
  { value: "30", label: "Últimos 30 días" },
  { value: "90", label: "Últimos 90 días" },
  { value: "180", label: "Últimos 6 meses" },
  { value: "365", label: "Último año" },
];

const moneyTick = (v) => formatMoney(v).replace(/\s/g, "");
const tooltipFormatter = (value, name) => [formatMoney(value), name];

const KpiCard = ({ label, value, icon: Icon, tone = "blue", subtitle, testid }) => {
  const toneClass = {
    blue: "bg-blue-50 text-[rgb(0,47,167)]",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  }[tone] || "bg-slate-50 text-slate-700";
  return (
    <Card className="bg-white border border-slate-200 rounded-sm p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">{label}</p>
          <p className="text-2xl font-black tracking-tight truncate" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            {value}
          </p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-sm ${toneClass} flex-shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
};

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-3">
    <div className="p-2 rounded-sm bg-blue-50 text-[rgb(0,47,167)]">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <h3 className="text-base font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

const EmptyState = ({ msg }) => (
  <div className="text-center py-10 text-sm text-slate-400">{msg}</div>
);

export default function Analytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Independent filters for the two "estrella" sections. "__all__" = no filter.
  const [proveedorFilter, setProveedorFilter] = useState("__all__");
  const [vendedorFilter, setVendedorFilter] = useState("__all__");
  // Unfiltered dataset — we keep one fresh copy for the dropdown options so
  // the lists don't shrink as soon as a filter is applied.
  const [optionsData, setOptionsData] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ days, limit: "10" });
    if (proveedorFilter !== "__all__") params.set("proveedor", proveedorFilter);
    if (vendedorFilter !== "__all__")  params.set("vendedor", vendedorFilter);
    api
      .get(`/analytics/sales?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Error al cargar analítica"))
      .finally(() => setLoading(false));
  }, [days, proveedorFilter, vendedorFilter]);

  // Reload the option-source dataset whenever the date range changes —
  // independent of the filter selectors so they don't self-exclude.
  useEffect(() => {
    api.get(`/analytics/sales?days=${days}&limit=50`)
      .then((r) => setOptionsData(r.data))
      .catch(() => {});
  }, [days]);

  // Distinct supplier / seller names from the unfiltered dataset.
  const proveedorOptions = useMemo(() => {
    const set = new Set();
    for (const p of (optionsData?.productos_estrella || [])) {
      if (p.proveedor) set.add(p.proveedor);
    }
    for (const v of (optionsData?.ventas_proveedor || [])) {
      if (v.proveedor && v.proveedor !== "Sin proveedor") set.add(v.proveedor);
    }
    return Array.from(set).sort();
  }, [optionsData]);

  const vendedorOptions = useMemo(() => {
    const set = new Set();
    for (const v of (optionsData?.ventas_vendedor || [])) {
      if (v.vendedor && v.vendedor !== "Sin vendedor asignado") set.add(v.vendedor);
    }
    return Array.from(set).sort();
  }, [optionsData]);

  const resumen = data?.resumen || {};

  const productosChart = useMemo(
    () => (data?.productos_estrella || []).map((p) => ({
      name: p.producto?.slice(0, 22) || p.codigo,
      ingresos: p.ingresos,
      cantidad: p.cantidad,
    })),
    [data],
  );

  const clientesChart = useMemo(
    () => (data?.clientes_estrella || []).map((c) => ({
      name: c.nombre.slice(0, 26),
      total: c.total,
      num_ordenes: c.num_ordenes,
    })),
    [data],
  );

  const cantonesChart = useMemo(
    () => (data?.cantones_top || []).map((c) => ({
      name: c.canton,
      provincia: c.provincia,
      total: c.total,
      num_ordenes: c.num_ordenes,
    })),
    [data],
  );

  const ticketCantonChart = useMemo(
    () => (data?.ticket_promedio_canton || []).map((c) => ({
      name: c.canton,
      ticket: c.ticket_promedio,
    })),
    [data],
  );

  const proveedorChart = useMemo(
    () => (data?.ventas_proveedor || []).map((p, i) => ({
      name: p.proveedor,
      total: p.total,
      color: COLORS[i % COLORS.length],
    })),
    [data],
  );

  const vendedorChart = useMemo(
    () => (data?.ventas_vendedor || []).map((v, i) => ({
      name: v.codigo ? `${v.vendedor} (${v.codigo})` : v.vendedor,
      total: v.total,
      num_ordenes: v.num_ordenes,
      ticket: v.ticket_promedio,
      color: v.color || COLORS[i % COLORS.length],
      registrado: v.registrado,
    })),
    [data],
  );

  const serieMes = useMemo(
    () => (data?.ventas_acumuladas_mes || []).map((d) => ({
      fecha: d.fecha.slice(5),
      total: d.total,
      acumulado: d.acumulado,
    })),
    [data],
  );

  return (
    <div data-testid="analytics-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-7 w-7 text-[rgb(0,47,167)]" />
          <div>
            <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Analítica de Ventas
            </h2>
            <p className="text-slate-500 text-sm mt-0.5">
              Conocé tus productos estrella, clientes top, cantones con más venta y la evolución del mes en curso.
            </p>
          </div>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="rounded-sm h-9 w-48" data-testid="analytics-range-select">
            <Calendar className="h-3.5 w-3.5 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-20 text-slate-500">Cargando analítica...</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="analytics-kpis">
            <KpiCard
              label="Ventas del mes"
              value={formatMoney(resumen.total_mes || 0)}
              icon={TrendingUp}
              tone="emerald"
              subtitle={`${resumen.num_ordenes_mes || 0} ${resumen.num_ordenes_mes === 1 ? "orden" : "órdenes"}`}
              testid="kpi-month-total"
            />
            <KpiCard
              label={`Ventas (${days} días)`}
              value={formatMoney(resumen.total_periodo || 0)}
              icon={ShoppingBag}
              subtitle={`${resumen.num_ordenes_periodo || 0} ${resumen.num_ordenes_periodo === 1 ? "orden" : "órdenes"}`}
              testid="kpi-period-total"
            />
            <KpiCard
              label="Ticket promedio"
              value={formatMoney(resumen.ticket_promedio_global || 0)}
              icon={Crown}
              tone="amber"
              subtitle={`Global del período`}
              testid="kpi-avg-ticket"
            />
            <KpiCard
              label="Cantones activos"
              value={(data?.cantones_top || []).length}
              icon={MapPin}
              tone="rose"
              subtitle="Con al menos 1 venta"
              testid="kpi-cantones"
            />
          </div>

          {/* Returns + net sales — only surface when there has been at least
              one devolución in the current window. Sits as a secondary KPI
              strip so the primary metrics above stay clean. */}
          {(resumen.devoluciones_periodo || 0) > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="analytics-kpis-returns">
              <KpiCard
                label={`Devoluciones (${days} días)`}
                value={`-${formatMoney(resumen.devoluciones_periodo || 0)}`}
                icon={ShoppingBag}
                tone="rose"
                subtitle={`${resumen.num_devoluciones_periodo || 0} devolución${(resumen.num_devoluciones_periodo || 0) === 1 ? "" : "es"}`}
                testid="kpi-period-returns"
              />
              <KpiCard
                label={`Ventas netas (${days} días)`}
                value={formatMoney(resumen.ventas_netas_periodo || 0)}
                icon={TrendingUp}
                tone="emerald"
                subtitle="Bruto − devoluciones"
                testid="kpi-period-net"
              />
              <KpiCard
                label="Devoluciones del mes"
                value={`-${formatMoney(resumen.devoluciones_mes || 0)}`}
                icon={ShoppingBag}
                tone="rose"
                subtitle={`${resumen.num_devoluciones_mes || 0} devolución${(resumen.num_devoluciones_mes || 0) === 1 ? "" : "es"}`}
                testid="kpi-month-returns"
              />
              <KpiCard
                label="Ventas netas del mes"
                value={formatMoney(resumen.ventas_netas_mes || 0)}
                icon={TrendingUp}
                tone="emerald"
                subtitle="Bruto − devoluciones"
                testid="kpi-month-net"
              />
            </div>
          )}

          {/* Ventas acumuladas del mes */}
          <Card className="bg-white border border-slate-200 rounded-sm p-5 mb-6" data-testid="chart-month-cumulative">
            <SectionTitle
              icon={TrendingUp}
              title="Ventas acumuladas del mes"
              subtitle="Diario y acumulado desde el día 1 del mes en curso"
            />
            {serieMes.length === 0 ? (
              <EmptyState msg="Aún no hay ventas registradas este mes." />
            ) : (
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart data={serieMes} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="acumGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#002FA7" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#002FA7" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={moneyTick} width={70} />
                    <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="acumulado" name="Acumulado" stroke="#002FA7" strokeWidth={2} fill="url(#acumGrad)" />
                    <Line type="monotone" dataKey="total" name="Día" stroke="#10B981" strokeWidth={1.6} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Productos estrella */}
            <Card className="bg-white border border-slate-200 rounded-sm p-5" data-testid="chart-top-products">
              <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                <SectionTitle icon={Crown} title="Productos estrella" subtitle="Top 10 por ingresos del período" />
                <Select value={proveedorFilter} onValueChange={setProveedorFilter}>
                  <SelectTrigger className="w-[200px] rounded-sm h-8 text-xs" data-testid="filter-productos-proveedor">
                    <SelectValue placeholder="Proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los proveedores</SelectItem>
                    {proveedorOptions.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {proveedorFilter !== "__all__" && (
                <p className="text-[11px] text-slate-500 mb-2 inline-flex items-center gap-1" data-testid="productos-filter-chip">
                  Filtrado por <span className="font-semibold text-[rgb(0,47,167)]">{proveedorFilter}</span>
                  <button onClick={() => setProveedorFilter("__all__")} className="text-rose-600 hover:underline ml-1">× quitar</button>
                </p>
              )}
              {productosChart.length === 0 ? <EmptyState msg={proveedorFilter === "__all__" ? "Sin ventas en el período." : "Sin ventas del proveedor en el período."} /> : (
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={productosChart} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={moneyTick} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#0F172A" }} width={140} />
                      <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Bar dataKey="ingresos" name="Ingresos" fill="#002FA7" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            {/* Clientes estrella */}
            <Card className="bg-white border border-slate-200 rounded-sm p-5" data-testid="chart-top-customers">
              <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                <SectionTitle icon={Users} title="Clientes estrella" subtitle="Top 10 por monto comprado" />
                <Select value={vendedorFilter} onValueChange={setVendedorFilter}>
                  <SelectTrigger className="w-[200px] rounded-sm h-8 text-xs" data-testid="filter-clientes-vendedor">
                    <SelectValue placeholder="Vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos los vendedores</SelectItem>
                    {vendedorOptions.map(v => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {vendedorFilter !== "__all__" && (
                <p className="text-[11px] text-slate-500 mb-2 inline-flex items-center gap-1" data-testid="clientes-filter-chip">
                  Filtrado por <span className="font-semibold text-emerald-700">{vendedorFilter}</span>
                  <button onClick={() => setVendedorFilter("__all__")} className="text-rose-600 hover:underline ml-1">× quitar</button>
                </p>
              )}
              {clientesChart.length === 0 ? <EmptyState msg={vendedorFilter === "__all__" ? "Sin ventas en el período." : "El vendedor no tiene ventas en el período."} /> : (
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={clientesChart} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={moneyTick} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#0F172A" }} width={140} />
                      <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Bar dataKey="total" name="Compras" fill="#10B981" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            {/* Cantones top */}
            <Card className="bg-white border border-slate-200 rounded-sm p-5" data-testid="chart-top-cantones">
              <SectionTitle icon={MapPin} title="Cantones con más venta" subtitle="Por monto total acumulado" />
              {cantonesChart.length === 0 ? <EmptyState msg="Sin ventas en el período." /> : (
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={cantonesChart} margin={{ top: 4, right: 12, left: 0, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-25} textAnchor="end" height={56} />
                      <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={moneyTick} width={70} />
                      <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Bar dataKey="total" name="Ventas" fill="#F59E0B" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            {/* Ticket promedio por canton */}
            <Card className="bg-white border border-slate-200 rounded-sm p-5" data-testid="chart-avg-ticket-canton">
              <SectionTitle icon={Crown} title="Ticket promedio por cantón" subtitle="Promedio por orden en cada cantón" />
              {ticketCantonChart.length === 0 ? <EmptyState msg="Sin ventas en el período." /> : (
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={ticketCantonChart} margin={{ top: 4, right: 12, left: 0, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-25} textAnchor="end" height={56} />
                      <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={moneyTick} width={70} />
                      <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Bar dataKey="ticket" name="Ticket promedio" fill="#8B5CF6" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          {/* Ventas por proveedor: pie + table */}
          <Card className="bg-white border border-slate-200 rounded-sm p-5 mt-4" data-testid="chart-suppliers">
            <SectionTitle icon={Truck} title="Ventas por proveedor" subtitle="Cuánto generó cada proveedor a través de sus productos" />
            {proveedorChart.length === 0 ? <EmptyState msg="Sin ventas en el período." /> : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={proveedorChart}
                        dataKey="total"
                        nameKey="name"
                        cx="50%" cy="50%"
                        innerRadius={55} outerRadius={95}
                        paddingAngle={2}
                        labelLine={false}
                      >
                        {proveedorChart.map((d, i) => (
                          <Cell key={i} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="suppliers-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="py-2 text-left font-medium text-slate-600 text-xs uppercase tracking-wider">Proveedor</th>
                        <th className="py-2 text-right font-medium text-slate-600 text-xs uppercase tracking-wider">Ventas</th>
                        <th className="py-2 text-right font-medium text-slate-600 text-xs uppercase tracking-wider">Unidades</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ventas_proveedor.map((p, i) => (
                        <tr key={p.proveedor + i} className="border-b border-slate-100">
                          <td className="py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                              <span className="truncate">{p.proveedor}</span>
                            </div>
                          </td>
                          <td className="py-2.5 text-right font-medium">{formatMoney(p.total)}</td>
                          <td className="py-2.5 text-right text-slate-500">{Math.round(p.num_unidades)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>

          {/* Ventas por vendedor */}
          <Card className="bg-white border border-slate-200 rounded-sm p-5 mt-4" data-testid="chart-vendors">
            <SectionTitle
              icon={UserCheck}
              title="Ventas por vendedor"
              subtitle="Performance del equipo comercial. Los registrados en Mi Empresa aparecen con su color y código."
            />
            {vendedorChart.length === 0 ? (
              <EmptyState msg="Sin ventas en el período." />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={vendedorChart} layout="vertical" margin={{ top: 4, right: 20, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={moneyTick} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#0F172A" }} width={160} />
                      <Tooltip formatter={tooltipFormatter} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                      <Bar dataKey="total" name="Ventas" radius={[0, 2, 2, 0]}>
                        {vendedorChart.map((v, i) => (
                          <Cell key={i} fill={v.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="vendors-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="py-2 text-left font-medium text-slate-600 text-xs uppercase tracking-wider">Vendedor</th>
                        <th className="py-2 text-right font-medium text-slate-600 text-xs uppercase tracking-wider">Ventas</th>
                        <th className="py-2 text-right font-medium text-slate-600 text-xs uppercase tracking-wider">Órdenes</th>
                        <th className="py-2 text-right font-medium text-slate-600 text-xs uppercase tracking-wider">Ticket prom.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.ventas_vendedor || []).map((v, i) => (
                        <tr key={v.vendedor + i} className="border-b border-slate-100" data-testid={`vendor-row-${i}`}>
                          <td className="py-2.5">
                            <div className="flex items-center gap-2">
                              <div className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: v.color || COLORS[i % COLORS.length] }} />
                              <span className="truncate">{v.vendedor}</span>
                              {v.codigo && (
                                <span className="text-[10px] font-mono text-slate-500">({v.codigo})</span>
                              )}
                              {!v.registrado && v.vendedor !== "Sin vendedor asignado" && (
                                <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-50 px-1 rounded" title="Texto libre, no asociado a un colaborador en Mi Empresa.">
                                  libre
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 text-right font-medium">{formatMoney(v.total)}</td>
                          <td className="py-2.5 text-right text-slate-500">{v.num_ordenes}</td>
                          <td className="py-2.5 text-right text-slate-500">{formatMoney(v.ticket_promedio)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
